/**
 * Ticket Display Component
 *
 * A concert-style ticket display for verified event registrations.
 * Shows ticket details with a visually appealing design.
 */

import type { EventRegistration, TicketData } from '@/types';
import { registrationApi, ApiClientError } from '@/services/api';
import { useState } from 'react';

/**
 * Props for the TicketDisplay component
 */
interface TicketDisplayProps {
  /** The event registration to display */
  registration: EventRegistration;
  /** Whether to show the download button */
  showDownload?: boolean;
  /** Optional CSS class name */
  className?: string;
}

/**
 * Get ticket data from registration
 */
function getTicketData(registration: EventRegistration): TicketData | null {
  return registration.ticketData;
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}

/**
 * TicketDisplay Component
 *
 * Displays a concert-style ticket with the registration details.
 * Features a visually appealing design with decorative elements.
 *
 * @example
 * ```tsx
 * <TicketDisplay registration={registration} showDownload />
 * ```
 */
export function TicketDisplay({
  registration,
  showDownload = true,
  className = '',
}: TicketDisplayProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const ticketData = getTicketData(registration);
  const eventDate = ticketData?.eventDate || 'June 13th, 2026';
  const eventTime = ticketData?.eventTime || '9:00 AM Onwards';
  const eventVenue = ticketData?.eventVenue || 'Venue TBA';

  /**
   * Handle ticket PDF download
   */
  const handleDownload = async () => {
    setIsDownloading(true);
    setDownloadError(null);

    try {
      const blob = await registrationApi.downloadTicket(registration.ticketNumber);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ReNav96-Ticket-${registration.ticketNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error instanceof ApiClientError) {
        setDownloadError(error.message);
      } else {
        setDownloadError('Failed to download ticket. Please try again.');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Main Ticket Card */}
      <div
        className="relative rounded-xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          border: '1px solid rgba(233, 69, 96, 0.3)',
        }}
      >
        {/* Ticket Header Band */}
        <div
          className="px-6 py-4"
          style={{
            background: 'linear-gradient(90deg, #e94560 0%, #d63952 100%)',
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-white/80 text-xs uppercase tracking-wider font-medium">
                30th Year Reunion
              </span>
              <h3 className="text-white text-2xl font-display font-bold">Re:Nav-96</h3>
            </div>
            <div className="text-right">
              <span className="text-white/70 text-xs">Navarasam School</span>
              <p className="text-white text-sm font-medium">Class of 1996</p>
            </div>
          </div>
        </div>

        {/* Ticket Body */}
        <div className="p-6">
          {/* Ticket Number */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
            <div>
              <span className="text-white/40 text-xs uppercase tracking-wider">Ticket Number</span>
              <p className="text-primary font-mono text-lg font-bold tracking-wider">
                {registration.ticketNumber}
              </p>
            </div>
            {/* QR Code Placeholder */}
            <div
              className="w-16 h-16 rounded-lg flex items-center justify-center"
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
              }}
            >
              <svg className="w-12 h-12 text-secondary" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 11h6v6H3v-6zm2 2v2h2v-2H5zm8-2h2v2h-2v-2zm4 0h2v2h-2v-2zm-4 4h2v2h-2v-2zm4 0h2v2h-2v-2zm0 4h2v2h-2v-2zm-4 0h2v2h-2v-2z"/>
              </svg>
            </div>
          </div>

          {/* Attendee Details */}
          <div className="space-y-4">
            {/* Name */}
            <div className="flex items-start gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(233, 69, 96, 0.2) 0%, rgba(233, 69, 96, 0.05) 100%)',
                }}
              >
                <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <span className="text-white/40 text-xs">Attendee</span>
                <p className="text-white font-medium">{registration.fullName}</p>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(233, 69, 96, 0.2) 0%, rgba(233, 69, 96, 0.05) 100%)',
                }}
              >
                <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <span className="text-white/40 text-xs">Email</span>
                <p className="text-white/70 text-sm">{registration.email}</p>
              </div>
            </div>

            {/* Phone (if available) */}
            {registration.phone && (
              <div className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'linear-gradient(135deg, rgba(233, 69, 96, 0.2) 0%, rgba(233, 69, 96, 0.05) 100%)',
                  }}
                >
                  <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <span className="text-white/40 text-xs">Phone</span>
                  <p className="text-white/70 text-sm">{registration.phone}</p>
                </div>
              </div>
            )}
          </div>

          {/* Divider with decorative dots */}
          <div className="relative my-6">
            <div className="border-t border-dashed border-white/20" />
            <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-secondary" />
            <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-4 h-4 rounded-full bg-secondary" />
          </div>

          {/* Event Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-white/40 text-xs uppercase tracking-wider">Date</span>
              <p className="text-white font-medium">{formatDate(eventDate)}</p>
            </div>
            <div>
              <span className="text-white/40 text-xs uppercase tracking-wider">Time</span>
              <p className="text-white font-medium">{eventTime}</p>
            </div>
            <div className="col-span-2">
              <span className="text-white/40 text-xs uppercase tracking-wider">Venue</span>
              <p className="text-white font-medium">{eventVenue}</p>
            </div>
          </div>
        </div>

        {/* Ticket Footer */}
        <div
          className="px-6 py-3 flex items-center justify-between"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
          }}
        >
          <p className="text-white/40 text-xs">
            Present this ticket at the venue
          </p>
          <div className="flex items-center gap-2">
            {registration.emailVerified && (
              <span className="inline-flex items-center gap-1 text-xs text-green-400">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Verified
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Download Button */}
      {showDownload && (
        <div className="space-y-2">
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {isDownloading ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Downloading...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download PDF Ticket
              </>
            )}
          </button>

          {downloadError && (
            <p className="text-red-400 text-sm text-center">{downloadError}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default TicketDisplay;
